import React, { useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import Drawer from './Drawer';
import { Input } from './ui/input';
import { MaskedInput } from './ui/masked-input';
import { Button } from './ui/button';
import useCepLookup from '../utils/useCepLookup';

export function emptySite() {
    return {
        name: '',
        zip_code: '',
        street: '',
        number: '',
        complement: '',
        neighborhood: '',
        city: '',
        state: '',
    };
}

function addressSummary(site) {
    const street = [site.street, site.number].filter(Boolean).join(', ');
    const place = [site.city, site.state].filter(Boolean).join('/');
    return [street, site.complement, site.neighborhood, place].filter(Boolean).join(' - ');
}

function SiteDrawer({ editing, onClose, onSave }) {
    const [draft, setDraft] = useState(editing.site);
    const [submitted, setSubmitted] = useState(false);

    const patch = (values) => setDraft((prev) => ({ ...prev, ...values }));
    const bind = (field) => ({
        value: draft[field] || '',
        onChange: (e) => patch({ [field]: e.target.value }),
    });

    const { cepLoading, cepError, handleCepChange } = useCepLookup((data) => {
        setDraft((prev) => ({
            ...prev,
            street: data.street || prev.street || '',
            neighborhood: data.neighborhood || prev.neighborhood || '',
            city: data.city || prev.city || '',
            state: data.state || prev.state || '',
        }));
    });

    const missing = (field) => submitted && !String(draft[field] || '').trim();
    const requiredFields = ['name', 'zip_code', 'street', 'number', 'neighborhood', 'city', 'state'];

    const handleSave = () => {
        setSubmitted(true);
        if (requiredFields.some((field) => !String(draft[field] || '').trim())) return;
        onSave(draft);
    };

    const fieldError = (field) => missing(field) && <p className="mt-1 text-xs text-rose-600">Campo obrigatório</p>;

    return (
        <Drawer
            open
            onClose={onClose}
            title={editing.index === null ? 'Nova obra' : 'Editar obra'}
            size="compact"
            footer={
                <div className="flex justify-end gap-3">
                    <Button type="button" variant="outline" onClick={onClose}>
                        Cancelar
                    </Button>
                    <Button type="button" onClick={handleSave}>
                        Salvar
                    </Button>
                </div>
            }
        >
            <div className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nome da obra *</label>
                    <Input {...bind('name')} />
                    {fieldError('name')}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">CEP *</label>
                        <MaskedInput
                            mask="cep"
                            value={draft.zip_code || ''}
                            onChange={(e) => {
                                patch({ zip_code: e.target.value });
                                handleCepChange(e.target.value);
                            }}
                        />
                        {cepLoading && <p className="mt-1 text-xs text-gray-500">Buscando endereço...</p>}
                        {cepError && <p className="mt-1 text-xs text-rose-600">{cepError}</p>}
                        {fieldError('zip_code')}
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Rua *</label>
                        <Input {...bind('street')} />
                        {fieldError('street')}
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Número *</label>
                        <Input {...bind('number')} />
                        {fieldError('number')}
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Complemento</label>
                        <Input {...bind('complement')} />
                    </div>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bairro *</label>
                    <Input {...bind('neighborhood')} />
                    {fieldError('neighborhood')}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Cidade *</label>
                        <Input {...bind('city')} />
                        {fieldError('city')}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">UF *</label>
                        <Input maxLength={2} {...bind('state')} />
                        {fieldError('state')}
                    </div>
                </div>
            </div>
        </Drawer>
    );
}

/** Lista de obras do cliente. As alterações vivem no formulário e são gravadas junto com o cliente. */
function ClientSitesEditor({ sites = [], onChange }) {
    const [editing, setEditing] = useState(null);

    const handleSave = (site) => {
        if (editing.index === null) {
            onChange([...sites, site]);
        } else {
            onChange(sites.map((current, i) => (i === editing.index ? site : current)));
        }
        setEditing(null);
    };

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">Obras</h3>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setEditing({ index: null, site: emptySite() })}
                >
                    <Plus className="w-4 h-4" />
                    Adicionar obra
                </Button>
            </div>

            {sites.length === 0 ? (
                <p className="rounded-lg border border-dashed border-gray-300 px-4 py-6 text-center text-sm text-gray-500">
                    Nenhuma obra cadastrada
                </p>
            ) : (
                <div className="space-y-2">
                    {sites.map((site, index) => (
                        <div
                            key={site.id ?? `new-${index}`}
                            className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3"
                        >
                            <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-medium text-gray-900">{site.name}</p>
                                {addressSummary(site) && (
                                    <p className="truncate text-xs text-gray-500">{addressSummary(site)}</p>
                                )}
                            </div>
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditing({ index, site: { ...emptySite(), ...site } })}
                                aria-label="Editar obra"
                            >
                                <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => onChange(sites.filter((_, i) => i !== index))}
                                aria-label="Remover obra"
                            >
                                <Trash2 className="w-4 h-4 text-rose-600" />
                            </Button>
                        </div>
                    ))}
                </div>
            )}

            {editing && (
                <SiteDrawer editing={editing} onClose={() => setEditing(null)} onSave={handleSave} />
            )}
        </div>
    );
}

export default ClientSitesEditor;
